import { cn } from "@/lib/utils";
import { Feather } from "lucide-react";

// Letterhead component for displaying speeches and songs in elegant format
const Letterhead = ({ children, title, subtitle, type = "speech", className }) => {
  return (
    <div 
      className={cn(
        "relative bg-[#FFFEF9] rounded-lg shadow-lg overflow-hidden",
        "border border-[#D4C5A9]",
        className
      )}
    >
      {/* Decorative corner elements */}
      <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-[#C5A059]/40 rounded-tl-lg" />
      <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-[#C5A059]/40 rounded-tr-lg" />
      <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-[#C5A059]/40 rounded-bl-lg" />
      <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-[#C5A059]/40 rounded-br-lg" />
      
      {/* Paper texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
        }}
      />
      
      {/* Header */}
      <div className="relative px-6 pt-6 pb-3 md:px-8 md:pt-8 md:pb-4 text-center border-b border-[#D4C5A9]/50">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-[#1B365D]/10 flex items-center justify-center">
            <Feather className="w-3 h-3 md:w-4 md:h-4 text-[#1B365D]" />
          </div>
          <span className="font-heading text-xs md:text-sm text-[#1B365D]/60 tracking-wider uppercase">
            MinFesttale
          </span>
        </div>
        
        {title && (
          <h3 className="font-heading text-xl md:text-2xl font-bold text-[#1B365D] mb-1">
            {title}
          </h3>
        )}
        
        {subtitle && (
          <p className="font-accent text-base md:text-lg text-[#C5A059]">
            {subtitle}
          </p>
        )}
      </div>
      
      {/* Content */}
      <div className="relative px-6 py-4 md:px-8 md:py-6">
        <div className="font-body text-[#1A1A1A] leading-relaxed whitespace-pre-line text-sm md:text-base">
          {children}
        </div>
      </div>
      
      {/* Footer decoration */}
      <div className="relative px-6 pb-4 pt-2 md:px-8 md:pb-6">
        <div className="flex items-center justify-center">
          <div className="h-px w-12 md:w-16 bg-[#C5A059]/40" />
          <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#C5A059]/40 mx-2 md:mx-3" />
          <div className="h-px w-12 md:w-16 bg-[#C5A059]/40" />
        </div>
      </div>
    </div>
  );
};

// ==================== EXAMPLE 1: WEDDING SPEECH ====================
export const EXAMPLE_WEDDING_FORM = {
  occasion: "Bryllup",
  recipient: "Anne & Thomas",
  speaker: "Mikkel (bedste ven til bruden)",
  tone: "Blanding af sjov og alvor",
  length: "Medium (~4 minutter)",
  how_they_met: "Til Henriks sommerfest i 2018. Thomas brugte halvanden time på at diskutere italiensk madlavning med Anne. Han vidste hvad soffritto var.",
  anecdote1: "Anne er umulig at imponere. I folkeskolen prøvede drengene alt muligt — hun kiggede bare op fra sin bog og sagde \"nå.\"",
  anecdote2: "Da jeg dumpede matematik i gymnasiet og var overbevist om at mit liv var ovre, mødte Anne op med pizza og en plan. Ikke trøst — en plan.",
  what_makes_them_great: "Thomas får Anne til at grine på en måde jeg aldrig har hørt. Anne får Thomas til at slappe af. De danser rundt om hinanden i køkkenet.",
  personality: "Anne er loyal, nysgerrig, smart, direkte. Thomas er rolig, varm, sjov på den stille måde.",
  wishes: "At de gør hinanden modigere."
};

export const EXAMPLE_WEDDING_SPEECH = `Kære Anne og Thomas,

Jeg har kendt Anne i over tyve år nu. Og i alle de år har hun haft én gennemgående egenskab: Hun er fuldstændig umulig at imponere. I folkeskolen kunne drengene stå på hænder, lave tricks på skateboard og prøve alt muligt — Anne kiggede bare op fra sin bog og sagde "nå."

Så da hun en dag ringede til mig og sagde "Mikkel, jeg har mødt én" — og hendes stemme lød anderledes — så vidste jeg, at det her var noget helt særligt.

Det var til Henriks sommerfest i 2018. Anne, du fortalte mig bagefter at Thomas havde brugt halvanden time på at diskutere italiensk madlavning med dig, og at han faktisk vidste hvad soffritto var. Jeg tror det var der, det skete. Ikke fordi han kendte soffritto — men fordi han var den første, der matchede din nysgerrighed.

Thomas, der er noget du skal vide om Anne. Når hun vælger nogen, så vælger hun dem for altid. Hun er den mest loyale person jeg kender. Dengang i gymnasiet, da jeg dumpede matematik og var overbevist om at mit liv var ovre, var det Anne der mødte op med pizza og en plan. "Vi laver en ny plan," sagde hun. Ikke "det går nok." En plan. Sådan er Anne.

Og I to sammen — det giver bare mening. Jeg har set jer til middage hvor I diskuterer som om I er de eneste i lokalet, og jeg har set jer i køkkenet hvor I danser rundt om hinanden som om I har gjort det i hundrede år. Thomas, du får Anne til at grine på en måde jeg aldrig har hørt før. Og Anne, du får Thomas til at slappe af på en måde hans familie siger de aldrig har set.

Der er et italiensk udtryk — "trovare la propria metà" — at finde sin anden halvdel. Jeg ved Anne hader klichéer, så jeg siger det på en anden måde: I gør ikke hinanden komplette. I var allerede komplette. I gør hinanden modigere.

Og det er det fineste jeg ved.

Kære gæster — vil I rejse jeres glas for Anne og Thomas. For kærlighed, for mod, og for at der altid er soffritto i køleskabet.

Skål!`;

// ==================== EXAMPLE 2: CONFIRMATION SPEECH ====================
export const EXAMPLE_CONFIRMATION_FORM = {
  occasion: "Konfirmation",
  recipient: "Frederik",
  speaker: "Peter (far)",
  tone: "Varm og kærlig",
  length: "Kort (~2 minutter)",
  child_age: "14 år",
  hobbies: "Fodbold (spiller målmand i BK Frem), elsker at bygge ting i Minecraft, og begyndt at spille guitar",
  anecdote1: "Da han var 6, byggede han en \"robot\" af papkasser og sagde den kunne rydde op på hans værelse. Den kunne den ikke, men idéen var der.",
  anecdote2: "Han stod for første gang i mål til en rigtig kamp. Lukkede 5 mål ind. Var ked af det i bilen hjem, men sagde \"jeg skal bare øve mig mere.\" Næste kamp holdt han buret rent.",
  proud_of: "Han giver aldrig op. Og han har en evne til at få andre til at føle sig velkomne. Han er altid ham der siger hej til den nye i klassen.",
  wishes: "At han skal vide at vi altid er her, uanset hvad. Og at det bedste ved ham ikke er det han kan, men den han er."
};

export const EXAMPLE_CONFIRMATION_SPEECH = `Kære Frederik,

I dag står du her — 14 år, konfirmand, og på vej ud i noget større. Og jeg står her som din far og tænker: Hvor blev tiden af?

Det føles som i går, at du sad på gulvet i stuen og byggede en robot af papkasser. Du var seks år. Robotten, forklarede du mig, kunne rydde op på dit værelse. Det kunne den ikke — det kan den stadig ikke — men idéen var der. Og den idé, den siger faktisk alt om dig: Du ser muligheder, hvor andre ser papkasser.

Der er et øjeblik jeg ofte tænker på. Din første rigtige kamp som målmand i BK Frem. Du lukkede fem mål ind. I bilen hjem var du stille, og jeg kunne se du var ked af det. Men så sagde du noget jeg aldrig glemmer: "Jeg skal bare øve mig mere." Ikke "det var uretfærdigt." Ikke "det var de andres skyld." Bare — "jeg skal øve mig mere." Næste kamp holdt du buret rent. Og der vidste jeg, at du har noget i dig som man ikke kan lære nogen. Det har man bare.

Men det jeg er allermest stolt af ved dig, Frederik, er ikke det du kan. Det er den du er. Du er ham der siger hej til den nye i klassen. Ham der rykker sig, så der er plads til én mere. Ham der mærker når nogen har en dårlig dag. Det er ikke noget du har lært — det er noget du er.

Om lidt skal du derude i en verden der nogle gange er stor og forvirrende og svær. Og der vil komme dage der føles som den kamp med de fem mål. Men husk det du sagde i bilen den dag. Og husk at uanset hvad — uanset hvad — så sidder din mor og jeg i den bil og venter på dig.

Vi er så stolte af dig.

Skål for Frederik.`;

// ==================== EXAMPLE 3: 60TH BIRTHDAY SPEECH ====================
export const EXAMPLE_BIRTHDAY_FORM = {
  occasion: "Rund fødselsdag (60 år)",
  recipient: "Hanne",
  speaker: "Lars (ægtefælle)",
  tone: "Blanding af sjov og alvor",
  length: "Medium (~4 minutter)",
  anecdote1: "Vores første date var i Tivoli. Jeg vandt en kæmpe bamse til hende i et skydespil. Fandt ud af 20 år senere at hun hadede den bamse, men hun havde den stående i 20 år fordi jeg havde vundet den.",
  anecdote2: "Da hun fyldte 40, besluttede hun sig for at lære at dykke. Hun er bange for fisk. Hun tog certifikatet alligevel. Hun dykker stadig ikke — men hun har certifikatet.",
  anecdote3: "Hun laver den bedste kartoffelsalat i familien. Det er et faktum. Der har været familiediskussioner om det. Hun vandt.",
  personality: "Stædig på den gode måde, varm, sjov uden at vide det, holder familien sammen, får alle til at føle sig hjemme",
  milestones: "Uddannet sygeplejerske, tre børn, farmor til to, har arbejdet på samme hospital i 30 år",
  wishes: "At hun fortjener at blive fejret. Hun gør altid alt for andre, og nu er det hendes tur."
};

export const EXAMPLE_BIRTHDAY_SPEECH = `Kære Hanne,

Der er noget du skal vide: Jeg har øvet mig på den her tale i tre uger. Jeg har skrevet den om fire gange. Og jeg er stadig nervøs, fordi — hvordan siger man 35 års kærlighed på fem minutter? Det kan man ikke. Men jeg prøver.

Vi startede i Tivoli. Vores første date, 1991. Jeg vandt en kæmpe bamse til dig i et skydespil. Jeg var så stolt. Du smilede og sagde "den er dejlig." Tyve år senere, da vi ryddede loftet, spurgte jeg til den bamse. "Lars," sagde du. "Den var det grimmeste jeg nogensinde har set." Men du havde haft den stående i tyve år. Fordi jeg havde vundet den. Og det, kære gæster — det er Hanne i en nøddeskal. Hun vælger kærlighed over alt andet. Selv over æstetik.

Og hun er stædig. Herregud, hun er stædig. Da hun fyldte 40, besluttede hun sig for at lære at dykke. Fantastisk, tænkte jeg. Bortset fra én ting: Hanne er bange for fisk. Ikke hajer. Fisk. De helt almindelige. Men hun tog kurset. Hun tog certifikatet. Og hun har — for ordens skyld — ikke dykket siden. Men certifikatet hænger i entréen. Fordi hun gjorde det.

Der er mange ting jeg beundrer ved dig, Hanne. Men det jeg beundrer mest, er noget de fleste slet ikke lægger mærke til. Det er den måde du holder os sammen på. Tredive års juleaftener, fødselsdage, søndagsmiddage — det er dig der ringer, dig der husker, dig der sørger for at alle er med. Tre børn, to børnebørn, og et hospital der ville falde fra hinanden uden dig. Du har arbejdet som sygeplejerske i tredive år, og jeg ved — fordi dine kollegaer har fortalt mig det — at du er den, patienterne spørger efter.

Og så er der kartoffelsalaten. Jeg nævner den, fordi det er vigtigt. Familien har diskuteret det, og resultatet er klart: Din kartoffelsalat er den bedste. Det er ikke en mening. Det er et faktum. Og din mor har accepteret det. Næsten.

Kære Hanne. Du gør altid alt for alle andre. Du er den der spørger "hvordan har du det?" og faktisk lytter til svaret. Du er den der bliver oppe til midnat for at pakke gaver, bage kager og skrive kort — fordi du vil have det skal være perfekt for alle.

Så i dag, på din 60-års fødselsdag, vil jeg sige noget du ikke hører tit nok: Det er din tur. I dag handler det om dig. Du fortjener det.

Kære gæster — vil I rejse glasset for Hanne. For 60 fantastiske år. For kartoffelsalat. For et dykkercertifikat der aldrig blev brugt. Og for den kvinde der holder os alle sammen.

Skål for Hanne!`;

export default Letterhead;
